import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";
import { DatePicker, Table, Typography, Card, Avatar } from "antd";
import { all_routes } from "../../../../../router/all_routes";
import LoadingSpinner from "../../../../shared/uiInterface/base-ui/LoadingSpinner";
import dayjs from "dayjs";
import breakService from "../../../../../services/break/break.service";
import { BreakHistoryUserDto, Break, BreakType } from "../../../../../types/break.types"; // Mola tiplerinizin doğru olduğundan emin olun
import { handleApiError } from "../../../../../utils/errorHandler";
import FooterSection from "../../../../shared/components/footerSection";
import i18n from "../../../../../i18n/config";
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
import { Table as AntTable, TableProps as AntdTableProps } from "antd";
import { User } from "../../../../../types/user.types";
import userService from "../../../../../services/user/user.service";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Legend } from 'recharts';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Dakikayı "X saat Y dakika" formatına çeviren yardımcı fonksiyon
const formatDurationFromMinutes = (minutes: number, t?: (key: string, fallback: string) => string): string => {
    if (isNaN(minutes) || minutes < 0) {
      return "-";
    }
    if (minutes === 0) {
      return `0 ${t ? t('common.time.minutesShort', 'dk') : 'dk'}`;
    }
  
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
  
    let durationString = "";
  
    if (hours > 0) {
      durationString += `${hours} ${t ? t('common.time.hoursShort', 'sa') : 'sa'}`;
    }
  
    if (remainingMinutes > 0) {
      if (hours > 0) {
        durationString += " ";
      }
      durationString += `${remainingMinutes} ${t ? t('common.time.minutesShort', 'dk') : 'dk'}`;
    }
    return durationString || (minutes === 0 ? `0 ${t ? t('common.time.minutesShort', 'dk') : 'dk'}` : "-");
  };


const AllBreakUser = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user');
    const [userBreakHistory, setUserBreakHistory] = useState<BreakHistoryUserDto[]>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [userName, setUserName] = useState<string>(t('breakUserAdminPage.employee', "Çalışan"));
    const [userData, setUserData] = useState<User | null>(null); // Kullanıcı verisini tutmak için state

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf('day'),
        dayjs().endOf('day')
    ]);

    const pickerLocale = useMemo(() => {
        return i18n.language === 'tr' ? datePickerTr : datePickerEn;
    }, [i18n.language]);

  

    const fetchUserBreaks = useCallback(async () => {
        if (!userId) {
            setError(t('breakUserAdminPage.errors.noUserId', "Kullanıcı ID bulunamadı."));
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            const numericUserId = Number(userId);
            // Kullanıcı bilgilerini ve mola geçmişini paralel çek
            const [userDataResponse, breaksData] = await Promise.all([
                userService.getUserById(numericUserId), // Kullanıcı bilgilerini çek
                breakService.getBreakHistoryUser( // getBreakHistoryUser parametre sırasını kontrol et
                    dateRange[0].format('YYYY-MM-DD'), 
                    dateRange[1].format('YYYY-MM-DD'), 
                    numericUserId
                )
            ]);
            
            setUserData(userDataResponse);
            setUserBreakHistory(breaksData || []); // breaksData null ise boş dizi ata

        } catch (err: any) {
            handleApiError(err, t);
            setError(t('breakUserAdminPage.errors.fetchError', "Mola verileri alınırken bir hata oluştu."));
            setUserBreakHistory([]);
        } finally {
            setLoading(false);
        }
    }, [userId, dateRange, t]);

    useEffect(() => {
        fetchUserBreaks();
    }, [fetchUserBreaks]);

    const handleDateRangeChange = (dates: any) => {
        if (dates && dates.length === 2) {
            setDateRange([dates[0], dates[1]]);
        } else {
            // Kullanıcı tarihleri temizlerse varsayılan aralığa dön
             setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
        }
    };
    
    const { calculatedTotalBreakMinutes, calculatedTotalBreaksCount, averageBreakMinutes } = useMemo(() => {
        if (!userBreakHistory) {
            return { calculatedTotalBreakMinutes: 0, calculatedTotalBreaksCount: 0, averageBreakMinutes: 0 };
        }
        const records = userBreakHistory || [];
        const totalMinutes = records.reduce((sum, record) => sum + (record.breakDuration || 0), 0);
        const totalCount = records.length;
        const avgMinutes = totalCount > 0 ? Math.round(totalMinutes / totalCount) : 0;
        return { 
            calculatedTotalBreakMinutes: totalMinutes, 
            calculatedTotalBreaksCount: totalCount, 
            averageBreakMinutes: avgMinutes 
        };
    }, [userBreakHistory]);


    const columns: AntdTableProps<BreakHistoryUserDto>['columns'] = [

        {
            title: t('breakUserAdminPage.table.date', "Tarih"),
            dataIndex: 'startTime',
            key: 'date',
            render: (text: string) => dayjs(text).isValid() ? dayjs(text).format('DD.MM.YYYY') : '-',
            sorter: (a: BreakHistoryUserDto, b: BreakHistoryUserDto) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix(),
        },
   // columns tanımının içinde
        {
            title: t('breakUserAdminPage.table.type', "Mola Tipi"),
            dataIndex: 'type',
            key: 'type',
            render: (text: string) => t(`breakTypes.${text}`, text),
            filters: userBreakHistory ? Array.from(new Set(userBreakHistory.map(r => r.type))).map(type => ({text: t(`breakTypes.${type}`, type), value: type })) : [],
            onFilter: (value: string | number | boolean | bigint, record: BreakHistoryUserDto) => {
                // Filtre değerlerimiz (value) mola tipi string'leri olduğu için,
                // record.type ile karşılaştırmadan önce String'e çevirmek güvenli olacaktır.
                return record.type === String(value);
            },
        },
        {
            title: t('breakUserAdminPage.table.startTime', "Başlangıç Saati"),
            dataIndex: 'startTime',
            key: 'startTime',
            render: (text: string) => dayjs(text).isValid() ? dayjs(text).format('HH:mm') : '-',
        },
        {
            title: t('breakUserAdminPage.table.endTime', "Bitiş Saati"),
            dataIndex: 'endTime',
            key: 'endTime',
            render: (text: string | null) => text && dayjs(text).isValid() ? dayjs(text).format('HH:mm') : '-',
        },
        {
            title: t('breakUserAdminPage.table.duration', "Süre"),
            dataIndex: 'breakDuration',
            key: 'duration',
            render: (minutes: number) => formatDurationFromMinutes(minutes, t),
            sorter: (a: BreakHistoryUserDto, b: BreakHistoryUserDto) => (a.breakDuration || 0) - (b.breakDuration || 0),
        },
    ];

    const getBreakTypeColor = (type: string) => {
        const colorMap: Record<string, string> = {
            "short_break": '#1890ff',     // Kısa Mola - Mavi
            "food_break": '#52c41a',      // Yemek Molası - Yeşil
            "personal_break": '#faad14',  // Kişisel Mola - Sarı
            "other_break": '#722ed1'      // Diğer Mola - Mor
        };
        // console.log('Gelen tip:', type, 'Dönen renk:', colorMap[type] || '#d9d9d9'); // Debug için
        return colorMap[type] || '#d9d9d9';
    };

    const breakAnalytics = useMemo(() => {
        if (!userBreakHistory || userBreakHistory.length === 0) {
            return {
                totalBreaks: 0,
                totalDuration: 0,
                averageDuration: 0,
                longestBreak: 0,
                shortestBreak: 0,
                mostUsedBreakType: '',
                breakTypeDistribution: [],
                dailyBreakTrend: [],
                hourlyDistribution: [],
                weekdayDistribution: []
            };
        }

        const records = userBreakHistory;
        const totalBreaks = records.length;
        const totalDuration = records.reduce((sum, record) => sum + (record.breakDuration || 0), 0);
        const averageDuration = Math.round(totalDuration / totalBreaks);
        
        // En uzun ve en kısa mola
        const durations = records.map(r => r.breakDuration || 0).filter(d => d > 0);
        const longestBreak = durations.length > 0 ? Math.max(...durations) : 0;
        const shortestBreak = durations.length > 0 ? Math.min(...durations) : 0;

        // Mola tipi dağılımı
        const typeCount: Record<string, number> = {};
        records.forEach(record => {
            typeCount[record.type] = (typeCount[record.type] || 0) + 1;
        });

        const breakTypeDistribution = Object.entries(typeCount).map(([type, count]) => {
            const color = getBreakTypeColor(type);
            // console.log('Mola tipi:', type, 'Renk:', color); // Debug için
            return {
                name: t(`breakTypes.${type}`, type),
                type: type,
                value: count,
                percentage: Math.round((count / totalBreaks) * 100),
                color: color
            };
        });

        const mostUsedBreakType = Object.entries(typeCount).reduce((a, b) => 
            typeCount[a[0]] > typeCount[b[0]] ? a : b
        )[0];

        // Günlük trend
        const dailyData: Record<string, { count: number; duration: number }> = {};
        records.forEach(record => {
            const date = dayjs(record.startTime).format('DD/MM');
            if (!dailyData[date]) {
                dailyData[date] = { count: 0, duration: 0 };
            }
            dailyData[date].count += 1;
            dailyData[date].duration += record.breakDuration || 0;
        });

        const dailyBreakTrend = Object.entries(dailyData)
            .map(([date, data]) => ({
                date,
                count: data.count,
                duration: data.duration,
                average: Math.round(data.duration / data.count)
            }))
            .sort((a, b) => dayjs(a.date, 'DD/MM').unix() - dayjs(b.date, 'DD/MM').unix());

        // Saatlik dağılım
        const hourlyData: Record<number, number> = {};
        records.forEach(record => {
            const hour = dayjs(record.startTime).hour();
            hourlyData[hour] = (hourlyData[hour] || 0) + 1;
        });

        const hourlyDistribution = Array.from({ length: 24 }, (_, hour) => ({
            hour: `${hour.toString().padStart(2, '0')}:00`,
            count: hourlyData[hour] || 0
        })).filter(item => item.count > 0);

        return {
            totalBreaks,
            totalDuration,
            averageDuration,
            longestBreak,
            shortestBreak,
            mostUsedBreakType,
            breakTypeDistribution,
            dailyBreakTrend,
            hourlyDistribution
        };
    }, [userBreakHistory, t]);

    // Mola tipi renkleri


    if (loading && !userBreakHistory) {
        return (
            <div className="page-wrapper">
                <div className="content">
                    <LoadingSpinner centered />
                </div>
            </div>
        );
    }

  

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Breadcrumb */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
                    <div className="my-auto">
                        <Title level={3} className="mb-1">{userData?.firstName} {userData?.lastName} - {t('breakUserAdminPage.title', "Mola Geçmişi")}</Title>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.employeeList}>{t('breakUserAdminPage.employeeList', "Çalışan Listesi")}</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {t('breakUserAdminPage.title', "Mola Geçmişi")}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap mt-3 mt-md-0">
                        <div className="me-2">
                            <RangePicker
                                locale={pickerLocale}
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                format="DD.MM.YYYY"
                                allowClear
                            />
                        </div>
                        {/* İleride export vb. butonlar eklenebilir */}
                    </div>
                </div>

                {/* Mola İstatistik Kartları */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-md-6 mb-3">
                        <Card bordered={false} className="h-100 shadow-sm hover-shadow">
                            <div className="text-center p-3">
                                <Avatar size={64} icon={<i className="ti ti-coffee text-primary" style={{fontSize: '32px'}}/>} className="bg-primary-subtle mb-3" />
                                <Title level={2} className="mb-1 text-primary">
                                    {breakAnalytics.totalBreaks}
                                </Title>
                                <Text type="secondary">Toplam Mola Sayısı</Text>
                            </div>
                        </Card>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-3">
                        <Card bordered={false} className="h-100 shadow-sm hover-shadow">
                            <div className="text-center p-3">
                                <Avatar size={64} icon={<i className="ti ti-clock text-info" style={{fontSize: '32px'}}/>} className="bg-info-subtle mb-3" />
                                <Title level={2} className="mb-1 text-info">
                                    {formatDurationFromMinutes(breakAnalytics.totalDuration, t)}
                                </Title>
                                <Text type="secondary">Toplam Mola Süresi</Text>
                            </div>
                        </Card>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-3">
                        <Card bordered={false} className="h-100 shadow-sm hover-shadow">
                            <div className="text-center p-3">
                                <Avatar size={64} icon={<i className="ti ti-chart-pie text-warning" style={{fontSize: '32px'}}/>} className="bg-warning-subtle mb-3" />
                                <Title level={2} className="mb-1 text-warning">
                                    {formatDurationFromMinutes(breakAnalytics.averageDuration, t)}
                                </Title>
                                <Text type="secondary">Ortalama Mola Süresi</Text>
                            </div>
                        </Card>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-3">
                        <Card bordered={false} className="h-100 shadow-sm hover-shadow">
                            <div className="text-center p-3">
                                <Avatar size={64} icon={<i className="ti ti-star text-success" style={{fontSize: '32px'}}/>} className="bg-success-subtle mb-3" />
                                <Title level={2} className="mb-1 text-success">
                                    {t(`breakTypes.${breakAnalytics.mostUsedBreakType}`, breakAnalytics.mostUsedBreakType || '-')}
                                </Title>
                                <Text type="secondary">En Çok Kullanılan Tip</Text>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Ek İstatistik Kartları */}
                {/* <div className="row mb-4"> */}
                    {/* <div className="col-xl-6 col-12 mb-3">
                        <Card bordered={false} className="h-100">
                            <div className="card-header">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <i className="ti ti-trending-up me-2 text-info"></i>
                                    Mola Süresi Analizi
                                </h6>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-6 text-center">
                                        <Text type="secondary" className="d-block">En Uzun Mola</Text>
                                        <Title level={4} className="text-danger mb-0">
                                            {formatDurationFromMinutes(breakAnalytics.longestBreak, t)}
                                        </Title>
                                    </div>
                                    <div className="col-6 text-center">
                                        <Text type="secondary" className="d-block">En Kısa Mola</Text>
                                        <Title level={4} className="text-success mb-0">
                                            {formatDurationFromMinutes(breakAnalytics.shortestBreak, t)}
                                        </Title>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div> */}

                    {/* <div className="col-xl-6 col-12 mb-3">
                        <Card bordered={false} className="h-100">
                            <div className="card-header">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <i className="ti ti-chart-donut me-2 text-warning"></i>
                                    Mola Tipi Dağılımı
                                </h6>
                            </div>
                            <div className="card-body">
                                {breakAnalytics.breakTypeDistribution.map((item, index) => (
                                    <div key={index} className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                style={{ 
                                                    backgroundColor: item.color, 
                                                    width: 12, 
                                                    height: 12, 
                                                    borderRadius: '50%',
                                                    marginRight: 8
                                                }}
                                            />
                                            <Text className="mb-0">{item.name}</Text>
                                        </div>
                                        <div className="text-end">
                                            <Text strong>{item.value}</Text>
                                            <Text type="secondary" className="ms-1">({item.percentage}%)</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div> */}
                {/* </div> */}

                {/* Grafikler */}
                <div className="row mb-4">
                    {/* Günlük Mola Trendi */}
                    <div className="col-xl-8 col-12 mb-3">
                        <Card>
                            <div className="card-header">
                                <h5 className="card-title d-flex align-items-center mb-0">
                                    <i className="ti ti-chart-line me-2 text-primary"></i>
                                    Günlük Mola Trendi
                                </h5>
                            </div>
                            <div className="card-body">
                                {breakAnalytics.dailyBreakTrend.length === 0 ? (
                                    <div className="text-center py-4">
                                        <Text type="secondary">Bu tarih aralığında mola verisi bulunamadı</Text>
                                    </div>
                                ) : (
                                    <div style={{ width: '100%', height: '300px' }}>
                                        <ResponsiveContainer>
                                            <LineChart data={breakAnalytics.dailyBreakTrend}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" />
                                                <YAxis />
                                                <RechartsTooltip 
                                                    formatter={(value, name) => [
                                                        name === 'count' ? `${value} mola` : `${value} dakika`,
                                                        name === 'duration' ? 'Mola Sayısı' : 'Toplam Süre (dk)'
                                                    ]}
                                                />
                                                <Legend />
                                                {/* <Line
                                                    type="monotone" 
                                                    dataKey="count" 
                                                    stroke="#1890ff" 
                                                    strokeWidth={2}
                                                    name="Mola Sayısı"
                                                    dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                                                /> */}
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="duration" 
                                                    stroke="#52c41a" 
                                                    strokeWidth={2}
                                                    name="Toplam Süre (dk)"
                                                    dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>

                    {/* Mola Tipi Dağılım Pasta Grafiği */}
                    <div className="col-xl-4 col-12 mb-3">
                        <Card>
                            <div className="card-header">
                                <h5 className="card-title d-flex align-items-center mb-0">
                                    <i className="ti ti-chart-pie me-2 text-warning"></i>
                                    Mola Tipi Dağılımı
                                </h5>
                            </div>
                            <div className="card-body">
                                {breakAnalytics.breakTypeDistribution.length === 0 ? (
                                    <div className="text-center py-4">
                                        <Text type="secondary">Mola verisi bulunamadı</Text>
                                    </div>
                                ) : (
                                    <div style={{ width: '100%', height: '300px' }}>
                                        <ResponsiveContainer>
                                            <PieChart>
                                                <Pie
                                                    dataKey="value"
                                                    data={breakAnalytics.breakTypeDistribution}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={40}
                                                    outerRadius={80}
                                                    paddingAngle={5}
                                                    label={({ percentage }) => percentage > 5 ? `${percentage}%` : ''}
                                                >
                                                    {breakAnalytics.breakTypeDistribution.map((entry, index) => {
                                                        const colors = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#f5222d', '#fa8c16'];
                                                        return (
                                                            <Cell 
                                                                key={`cell-${index}`} 
                                                                fill={entry.color || colors[index % colors.length]} 
                                                            />
                                                        );
                                                    })}
                                                </Pie>
                                                <RechartsTooltip formatter={(value, name) => [`${value} mola`, name]} />
                                                <Legend 
                                                    formatter={(value, entry) => (
                                                        <span style={{ color: entry.color }}>{value}</span>
                                                    )}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Saatlik Mola Dağılımı */}
                {breakAnalytics.hourlyDistribution.length > 0 && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <Card>
                                <div className="card-header">
                                    <h5 className="card-title d-flex align-items-center mb-0">
                                        <i className="ti ti-clock me-2 text-info"></i>
                                        Saatlik Mola Dağılımı
                                    </h5>
                                    <Text type="secondary" className="mb-0">Hangi saatlerde mola alındığı</Text>
                                </div>
                                <div className="card-body">
                                    <div style={{ width: '100%', height: '300px' }}>
                                        <ResponsiveContainer>
                                            <BarChart data={breakAnalytics.hourlyDistribution}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="hour" />
                                                <YAxis />
                                                <RechartsTooltip formatter={(value) => [`${value} mola`, 'Mola Sayısı']} />
                                                <Bar dataKey="count" fill="#faad14" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                )}

                {/* Mola Kayıtları Listesi */}
                <Card>
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
                        <h5 className="card-title mb-0">{t('breakUserAdminPage.breakRecordsList', "Mola Kayıtları")}</h5>
                        {/* Filtreler veya arama çubuğu eklenebilir */}
                    </div>
                    <div className="card-body">
                        {/* {loading ? (
                            <LoadingSpinner centered />
                        ) :
                        ( */}
                            <Table 
                                dataSource={userBreakHistory}
                                columns={columns}
                                // rowKey="breakId" // breakId'nin BreakDto içinde benzersiz olduğundan emin olun
                                pagination={{ pageSize: 10, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ${t('common.table.records', 'kayıt')}` }}
                                className="table-striped table-hover"
                                scroll={{ x: 'max-content' }} // Yatay kaydırma için
                            />
                        {/* )} */}
                    </div>
                </Card>
            </div>

            {/* Footer */}
            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 mt-4">
                <FooterSection />
            </div>
        </div>
    );
};

export default AllBreakUser;
